import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import ky, { HTTPError } from "ky";
import { buttonVariants } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/passwordInput";
import { passwordSchema } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { requiredPassword } from "@/lib/utils/zod";
import { env } from "@/env";

const resetPasswordSearchSchema = z.object({
  token: z.string(),
});

export const Route = createFileRoute("/reset-password")({
  component: RouteComponent,
  validateSearch: zodValidator(resetPasswordSearchSchema),
  staticData: {
    title: "Reset Password",
  },
});

const PasswordResetFormSchema = z
  .object({
    new_password: passwordSchema,
    confirm_password: requiredPassword("Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });
type PasswordResetFormSchema = z.infer<typeof PasswordResetFormSchema>;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  const form = useForm<PasswordResetFormSchema>({
    resolver: zodResolver(PasswordResetFormSchema),
    defaultValues: { new_password: "", confirm_password: "" },
    mode: "onChange",
  });
  const newPassword = form.watch("new_password");
  const confirmPassword = form.watch("confirm_password");

  useEffect(() => {
    if (confirmPassword) void form.trigger("confirm_password");
  }, [form, newPassword]);

  const updatePassword = useMutation({
    async mutationFn({ new_password }: PasswordResetFormSchema) {
      await ky.post("api/v1/auth/update-password", {
        baseUrl: env.VITE_AUTH_API_URL,
        json: {
          token: searchParams.token,
          new_password,
        },
      });
    },
    onSuccess() {
      navigate({ to: "/auth/login" });
    },
    onError(error) {
      if (error instanceof HTTPError) {
        const body: unknown = error.data;
        const detail =
          body && typeof body === "object" && "detail" in body
            ? body.detail
            : null;

        if (Array.isArray(detail)) {
          for (const issue of detail) {
            if (!issue || typeof issue !== "object") continue;
            const location = "loc" in issue ? issue.loc : null;
            const message = "msg" in issue ? issue.msg : null;
            if (
              Array.isArray(location) &&
              location.at(-1) === "new_password" &&
              typeof message === "string"
            ) {
              form.setError("new_password", {
                type: "server",
                message,
              });
              return;
            }
          }
        }

        if (typeof detail === "string") {
          form.setError("root.serverError", { message: detail });
          return;
        }
      }

      form.setError("root.serverError", {
        message: "Failed to update password. Please try again.",
      });
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-8">
      <div className="w-full space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold">Update Password</h1>
          <p className="text-gray-500">Please kindly set your new password</p>
        </div>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={(event) => {
              const fields = new FormData(event.currentTarget);
              const newPassword = fields.get("new_password");
              const confirmPassword = fields.get("confirm_password");

              if (
                typeof newPassword === "string" &&
                typeof confirmPassword === "string" &&
                newPassword.length > 0 &&
                confirmPassword.length > 0 &&
                newPassword !== confirmPassword
              ) {
                event.preventDefault();
                form.setError("confirm_password", {
                  type: "validate",
                  message: "Passwords do not match",
                });
                return;
              }

              void form.handleSubmit((formData) => {
                if (updatePassword.isPending) return;
                form.clearErrors("root.serverError");
                updatePassword.mutate(formData);
              })(event);
            }}
          >
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="New Password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* Confirm New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Confirm New Password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <button
              type="submit"
              disabled={
                updatePassword.isPending ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword
              }
              className="w-full cursor-pointer rounded-md bg-[#dd2590] py-3 font-medium text-white transition-colors hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updatePassword.isPending ? "Updating.." : "Update Password"}
            </button>

            <Link
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "flex h-12 w-full items-center justify-center gap-2 text-sm",
              )}
              to="/auth/login"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Log in</span>
            </Link>

            {form.formState.errors.root?.serverError ? (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}
          </form>
        </Form>
      </div>
    </main>
  );
}
